import isNumber from 'lodash.isnumber';

import {normalizeSeparatorFormat} from '../utils/normalize-separator-format';
import {normalizeSpaceSeparator} from '../utils/normalize-space-separator';

import {
    correctValue,
    getDecimal,
    round,
    roundOff,
    truncateTrailingZeros,
} from './utils';

import type {Options} from './typedef';

export type Locale = {
    decimalFormat: string,
    thousandFormat: string,
};
const defaultLocale = {
    thousandFormat: 'COMMA',
    decimalFormat: 'PERIOD',
};

const defaultFormatOptions = {
    suffix: '',
    hideTrailingZeros: false,
};

/**
 * @name NumberFormat
 * @param {number} decimals - a count of numbers after the decimal separator
 * @param {boolean} showZeros - formatting 0 to an empty string
 * @param {boolean} [negativeParenthesis=false] - formatting negative values to positive
 * @param {string} [suffix=''] - suffix for a formatted value
 */
export class NumberFormat {
    decimals: number;
    showZeros: boolean;
    negativeParenthesis: boolean;
    suffix: string;


    constructor(
        decimals: number,
        showZeros: boolean,
        negativeParenthesis: boolean = false,
        suffix: string = '',
    ) {
        this.decimals = decimals || 0;
        this.showZeros = showZeros;
        this.negativeParenthesis = negativeParenthesis;
        this.suffix = suffix;
    }

    /**
     * @description Returns the user's locale-based formatted data for numeric input
     * and an empty string for no numeric input.
     * @param {number=} value - input value
     * @param {Locale} [locale={thousandFormat:'COMMA',decimalFormat:'PERIOD'}] - user's locale
     * @param {Options} options - format options
     * @return {string} - formatted value or empty string
     */
    format(
        value: number | null | undefined,
        locale: Locale = defaultLocale,
        options: Options = {},
    ) {
        options = {
            ...options,
            roundOff: !!options.roundOff,
            suffix: this.suffix,
        };
        if (isNumber(value) && (options.showZeros || this.showZeros || value !== 0)) {
            if (options.roundOff) {
                value = roundOff(value);
            }

            if (options.truncateDecimals) {
                value = round(value);
            }

            const decimals =
                options.decimals !== undefined
                    ? options.decimals
                    : this.decimals;
            const correctedValue = correctValue(value, decimals);

            if (
                (options.negativeParenthesis || this.negativeParenthesis) &&
                value < 0
            ) {
                return `(${this.formatNumber(
                    correctedValue,
                    locale,
                    options,
                ).slice(1)})`;
            }

            if (value < 0 && options.absoluteValue) {
                return this.formatNumber(
                    Math.abs(correctedValue),
                    locale,
                    options,
                );
            }

            return this.formatNumber(correctedValue, locale, options);
        }

        return '';
    }

    /**
     * @description returns formatted number, based on the user's locale
     * @param {number} value - input value
     * @param {Locale} locale - user's locale
     * @param {Options} options - format options
     * @return {string} - formatted value
     */
    formatNumber(
        value: number,
        locale: Locale = defaultLocale,
        options: Options,
    ) {
        const {thousandFormat, decimalFormat} = locale;
        const mergeOptions = {...defaultFormatOptions, ...options};

        // Convert format strings (enum or direct) to separator characters
        let thousandSeparator = normalizeSeparatorFormat(thousandFormat);
        let decimalSeparator = normalizeSeparatorFormat(decimalFormat);

        // Convert regular spaces to NBSP to prevent line wrapping
        thousandSeparator = normalizeSpaceSeparator(thousandSeparator);
        decimalSeparator = normalizeSpaceSeparator(decimalSeparator);

        // Apply global number formatting rules when separators are equal
        // Having the same separator for thousands and decimals would create invalid numbers (e.g., "1,234,56")
        // Default to comma for thousands and period for decimals (US format) in this case
        if (thousandSeparator === decimalSeparator) {
            thousandSeparator = ',';
            decimalSeparator = '.';
        }

        let decimals =
            mergeOptions.decimals !== undefined
                ? mergeOptions.decimals
                : this.decimals;

        if (typeof value !== 'number') {
            return '';
        }

        const sign = value < 0 ? '-' : '';
        value = Math.abs(round(Number(value) || 0, decimals));

        // @ts-expect-error
        let num = parseInt(value);

        const toRoundOff = mergeOptions.roundOff && value >= 1;

        if (toRoundOff) {
            num += value % 1 !== 0 ? Math.round(Math.abs(value - num)) : 0;
        } else {
            decimals = getDecimal(decimals, String(num), mergeOptions);
            const roundedDecimals = Number(
                Math.abs(value - num).toFixed(decimals),
            );

            if (roundedDecimals === 1) {
                num += roundedDecimals;
            }
        }

        const i = String(num);
        const j = i.length > 3 ? i.length % 3 : 0;
        const newValue =
            sign +
            (j ? i.substring(0, j) + thousandSeparator : '') +
            i.substring(j).replace(/(\d{3})(?=\d)/g, '$1' + thousandSeparator);

        const getValueWithDecimal = () => {
            let res = newValue;

            if (decimals) {
                const pattern = `(\\${decimalSeparator})([1-9]*)?0+$`;
                res = res
                    .concat(decimalSeparator)
                    .concat(
                        Math.abs(value - num)
                            .toFixed(decimals)
                            .slice(2),
                    )
                    .replace(
                        new RegExp(pattern),
                        truncateTrailingZeros(mergeOptions.hideTrailingZeros),
                    );
            }

            return res.concat(mergeOptions.suffix);
        };

        return toRoundOff ? newValue : getValueWithDecimal();
    }
}
